class CollectionFilters extends HTMLElement {
  // Constants
  static SELECTORS = {
    FILTER_PARENT: '#filtersCollapse',
    FILTER_FORM: 'form',
    QUICKSHOP: 'quick-shop',
    FILTER_TOGGLE: '.filter__toggle',
    FILTER_DRAWER: '#filter-drawer',
    SORTBY_DRAWER: '#sortby-drawer',
    SORT_DRAWER: '#sort-drawer',
    FILTERS_CLOSE: '.collection-filters-close',
    FILTER_CONTAINER: '.filter__container',
    SORTBY_OPTIONS: '[data-sortby] .sortby_options',
    PAGINATION_VALUE: '[data-custom-pagination] .pagination_value',
    APPLY_FILTERS: '[data-applyFilters]',
    APPLY_SORTBY: '[data-applySortBy]',
    CLEAR_SORTBY: '[data-clearSortBy]',
    SORTBY_OPTION: 'input[type="checkbox"].sortby-option',
    PAGINATION_LINKS: '[data-pagination]',
    FILTER_BACK: '[data-filter-back]',
    FILTER_LABEL: '[data-filter-label]'
  };

  static EVENTS = {
    CLICK: 'click',
    CHANGE: 'change',
    INPUT: 'input',
    SUBMIT: 'submit',
    KEYUP: 'keyup',
    POPSTATE: 'popstate'
  };

  static CLASSES = {
    OPEN: 'open',
    LOADING: 'loading',
    FILTER_ACTIVE: 'filter-active',
    MOBILEMENU_OPEN: 'mobilemenu-open',
    D_NONE: 'd-none'
  };

  /** @type {AbortController | null} */
  static _gridFetchController = null;

  /**
   * Count active filters: per-type badges in the drawer, Clear (i) labels, and overall total.
   * Document-scoped so it works after AJAX replaces filter markup.
   */
  static syncFilterSelectionCount() {
    const root = document.getElementById('main-collection-filters');
    const form = document.getElementById('CollectionFiltersForm');
    if (!form) return;

    let count = 0;
    form.querySelectorAll('input[type="checkbox"][data-tagslist]:checked').forEach(() => {
      count += 1;
    });
    form.querySelectorAll('input[type="checkbox"].collection-entry:checked').forEach(() => {
      count += 1;
    });

    const text = count > 0 ? String(count) : '';
    if (root) {
      root.querySelectorAll('i[data-totalProducts]').forEach((el) => {
        el.textContent = text;
      });
    }

    // Sub-collection "types" (mobile): count checked entries per [data-filter-type] panel
    form.querySelectorAll('[data-filter-subcategories][data-filter-type]').forEach((panel) => {
      const type = panel.getAttribute('data-filter-type');
      if (!type) return;
      const panelCount = panel.querySelectorAll('input.collection-entry:checked').length;
      const mainRow = Array.from(form.querySelectorAll('[data-filter-main-categories]')).find(
        (el) => el.getAttribute('data-filter-type') === type
      );
      const badge = mainRow?.querySelector('[data-subcollection-type-count]');
      if (badge) {
        badge.textContent = panelCount > 0 ? String(panelCount) : '';
        badge.classList.toggle(CollectionFilters.CLASSES.D_NONE, panelCount === 0);
        badge.setAttribute('aria-hidden', panelCount === 0 ? 'true' : 'false');
      }
    });

    // Tag filter groups (mobile): count checked tags under each .filter__title section
    const tagList = form.querySelector('.filter__list[role="list"]');
    if (tagList) {
      tagList.querySelectorAll('li.filter__title').forEach((titleLi) => {
        const badge = titleLi.querySelector('[data-tag-group-count]');
        if (!badge) return;
        let groupCount = 0;
        let sibling = titleLi.nextElementSibling;
        while (sibling && !sibling.classList.contains('filter__title')) {
          sibling.querySelectorAll('input[type="checkbox"][data-tagslist]:checked').forEach(() => {
            groupCount += 1;
          });
          sibling = sibling.nextElementSibling;
        }
        badge.textContent = groupCount > 0 ? String(groupCount) : '';
        badge.classList.toggle(CollectionFilters.CLASSES.D_NONE, groupCount === 0);
        badge.setAttribute('aria-hidden', groupCount === 0 ? 'true' : 'false');
      });
    }
  }

  constructor() {
    super();
    this.initializeProperties();
    this.bindMethods();
    this.initializeComponents();
  }

  /**
   * Initialize component properties from dataset and DOM elements
   */
  initializeProperties() {
    this.accordionType = this.dataset.accordiontype;
    this.filterParent = this.closest(CollectionFilters.SELECTORS.FILTER_PARENT);
    this.filterForm = this.querySelector(CollectionFilters.SELECTORS.FILTER_FORM);
    this.quickshop = document.querySelector(CollectionFilters.SELECTORS.QUICKSHOP);
    this.filterType = this.dataset.filtertype;
  }

  /**
   * Bind methods to correct context and setup debounced functions
   */
  bindMethods() {
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);
    this.debouncedOnSubmit = Utility.debounce(this.onSubmitHandler.bind(this), 500);
  }

  /**
   * Initialize all component parts in correct order
   */
  initializeComponents() {
    this.initializeFilterToggles();
    this.initializeDrawers();
    this.initializeHorizontalFilters();
    this.initializeSortAndPagination();
    this.initializeEventListeners();
    this.initializeApplyButtons();
    this.initializeSortButtons();
    this.initializePagination();
    this.finalizeInitialization();
  }

  /**
   * Initialize filter toggle buttons and accessibility attributes
   */
  initializeFilterToggles() {
    const toggleBtns = this.querySelectorAll(CollectionFilters.SELECTORS.FILTER_TOGGLE);
    toggleBtns.forEach(button => {
      button.addEventListener(CollectionFilters.EVENTS.CLICK, this.toggleFilterBlock.bind(this));
    });
    this.addAccessibilityAttributes(toggleBtns);
  }

  /**
   * Initialize filter and sort drawer elements and event listeners
   */
  initializeDrawers() {
    // Filter drawer
    this.openFilterDrawer = document.getElementById('filter-drawer');
    if (this.openFilterDrawer) {
      this.openFilterDrawer.addEventListener(CollectionFilters.EVENTS.CLICK, this.toggleFilterDrawer.bind(this));
    }

    // Sort drawer
    this.openSortbyDrawer = document.getElementById('sortby-drawer');
    if (this.openSortbyDrawer) {
      this.openSortbyDrawer.addEventListener(CollectionFilters.EVENTS.CLICK, this.toggleSortDrawer.bind(this));
    }

    // Close buttons
    this.closeFilterDrawer = this.filterParent?.querySelector(CollectionFilters.SELECTORS.FILTERS_CLOSE);
    if (this.closeFilterDrawer) {
      this.closeFilterDrawer.addEventListener(CollectionFilters.EVENTS.CLICK, this.toggleFilterDrawer.bind(this));
    }

    const sortDrawer = document.getElementById('sort-drawer');
    this.closeSortDrawer = sortDrawer?.querySelector(CollectionFilters.SELECTORS.FILTERS_CLOSE);
    if (this.closeSortDrawer) {
      this.closeSortDrawer.addEventListener(CollectionFilters.EVENTS.CLICK, this.toggleSortDrawer.bind(this));
    }
  }

  /**
   * Initialize horizontal filter behavior for click-outside-to-close functionality
   */
  initializeHorizontalFilters() {
    if (this.filterType === 'horizontal-filters') {
      document.body.addEventListener(CollectionFilters.EVENTS.CLICK, () => {
        const openFilters = document.querySelectorAll(`${CollectionFilters.SELECTORS.FILTER_CONTAINER}.${CollectionFilters.CLASSES.OPEN}`);
        openFilters.forEach((target) => {
          target.querySelector(CollectionFilters.SELECTORS.FILTER_TOGGLE)?.click();
        });
      });

      const filterContainers = document.querySelectorAll(CollectionFilters.SELECTORS.FILTER_CONTAINER);
      filterContainers.forEach((target) => {
        target.addEventListener(CollectionFilters.EVENTS.CLICK, (event) => {
          event.stopPropagation();
        });
      });
    }

    // Initialize click-outside-to-close for data-subcollections dropdowns
    if (typeof this.initializeSubcollectionDropdowns === 'function') {
      this.initializeSubcollectionDropdowns();
    }
  }

  /**
   * Initialize click-outside-to-close functionality for data-subcollections dropdowns
   */
  initializeSubcollectionDropdowns() {
    // Check if document and required methods exist
    if (!document || typeof document.addEventListener !== 'function' || typeof document.querySelectorAll !== 'function') {
      console.warn('Document or required methods not available');
      return;
    }

    // Remove existing listener if it exists to prevent duplicates
    if (this.subcollectionClickHandler && typeof document.removeEventListener === 'function') {
      document.removeEventListener(CollectionFilters.EVENTS.CLICK, this.subcollectionClickHandler);
    }

    // Create and store the event handler
    this.subcollectionClickHandler = (event) => {
      // Check if event and event.target exist
      if (!event || !event.target) {
        return;
      }

      const filterDropdowns = document.querySelectorAll('[data-subcollections]');
      
      // Check if filterDropdowns exists and has length
      if (!filterDropdowns || filterDropdowns.length === 0) {
        return;
      }
      
      filterDropdowns.forEach((dropdown) => {
        // Check if dropdown exists and has required methods
        if (!dropdown || typeof dropdown.contains !== 'function' || typeof dropdown.querySelector !== 'function') {
          return;
        }

        const isClickInside = dropdown.contains(event.target);
        
        // Look for various possible dropdown structures with safe checks
        let openDropdown = null;
        
        try {
          openDropdown = dropdown.querySelector('.dropdown.open') || 
                        dropdown.querySelector('.dropdown.show') || 
                        dropdown.querySelector('.open') ||
                        (dropdown.classList && dropdown.classList.contains('open') ? dropdown : null);
        } catch (error) {
          console.warn('Error finding open dropdown:', error);
          return;
        }
        
        if (!isClickInside && openDropdown) {
          // Debug logging (remove after testing)
         
          // Close the dropdown - try multiple approaches with safety checks
          if (openDropdown === dropdown) {
            // If the dropdown container itself has the open class
            if (dropdown.classList && typeof dropdown.classList.remove === 'function') {
              dropdown.classList.remove('open', 'show');
            }
          } else {
            // If a child element has the open class
            if (openDropdown.classList && typeof openDropdown.classList.remove === 'function') {
              openDropdown.classList.remove('open', 'show');
            }
          }
          
          // Update aria-expanded attribute if exists
          let dropdownToggle = null;
          try {
            dropdownToggle = dropdown.querySelector('[is="drop-down"]') || 
                            dropdown.querySelector('[data-toggle]') ||
                            dropdown.querySelector('button') ||
                            dropdown.querySelector('a');
          } catch (error) {
            console.warn('Error finding dropdown toggle:', error);
          }
          
          if (dropdownToggle && typeof dropdownToggle.setAttribute === 'function') {
            dropdownToggle.setAttribute('aria-expanded', false);
          }
          
          // Close any child dropdowns as well
          let childDropdowns = null;
          try {
            childDropdowns = dropdown.querySelectorAll('.dropdown.open, .open');
          } catch (error) {
            console.warn('Error finding child dropdowns:', error);
          }
          
          if (childDropdowns && childDropdowns.length > 0) {
            childDropdowns.forEach((child) => {
              // Check if child exists and has required methods
              if (!child || !child.classList || typeof child.classList.remove !== 'function') {
                return;
              }
              
              child.classList.remove('open', 'show');
              
              let childToggle = null;
              try {
                childToggle = child.querySelector('[is="drop-down"]') || 
                             child.querySelector('[data-toggle]') ||
                             child.querySelector('button') ||
                             child.querySelector('a');
              } catch (error) {
                console.warn('Error finding child toggle:', error);
              }
              
              if (childToggle && typeof childToggle.setAttribute === 'function') {
                childToggle.setAttribute('aria-expanded', false);
              }
            });
          }
        }
      });
    };

    // Add the event listener with a small delay to ensure DOM is ready
    setTimeout(() => {
      if (document && typeof document.addEventListener === 'function' && this.subcollectionClickHandler) {
        document.addEventListener(CollectionFilters.EVENTS.CLICK, this.subcollectionClickHandler);
        console.log('Subcollection click handler attached'); // Debug log
      } else {
        console.warn('Unable to attach subcollection click handler');
      }
    }, 100);
  }

  /**
   * Cleanup subcollection dropdown event listeners
   */
  cleanupSubcollectionDropdowns() {
    if (this.subcollectionClickHandler && 
        document && 
        typeof document.removeEventListener === 'function') {
      document.removeEventListener(CollectionFilters.EVENTS.CLICK, this.subcollectionClickHandler);
      this.subcollectionClickHandler = null;
    }
  }

  /**
   * Initialize sort and pagination controls with event listeners
   */
  initializeSortAndPagination() {
    this.sortbyValues = document.querySelectorAll(CollectionFilters.SELECTORS.SORTBY_OPTIONS);
    this.sortbyValues.forEach(input => {
      input.addEventListener(CollectionFilters.EVENTS.CHANGE, this.updateSortBy.bind(this));
    });

    this.paginationValues = document.querySelectorAll(CollectionFilters.SELECTORS.PAGINATION_VALUE);
    this.paginationValues.forEach(input => {
      input.addEventListener(CollectionFilters.EVENTS.CHANGE, this.updatePagination.bind(this));
    });
  }

  /**
   * Initialize global event listeners for keyboard and form interactions
   */
  initializeEventListeners() {
    if (this.filterParent) {
      this.filterParent.addEventListener(CollectionFilters.EVENTS.KEYUP, (event) => {
        if (event.code.toUpperCase() === 'ESCAPE') {
          this.closeFilterDrawer?.dispatchEvent(new Event(CollectionFilters.EVENTS.CLICK));
        }
      });
    }

    if (this.filterForm) {
      this.filterForm.addEventListener(CollectionFilters.EVENTS.INPUT, this.debouncedOnSubmit);
      this.filterForm.addEventListener(CollectionFilters.EVENTS.SUBMIT, this.debouncedOnSubmit);
      this.filterForm.addEventListener(CollectionFilters.EVENTS.INPUT, CollectionFilters.syncFilterSelectionCount);
      this.filterForm.addEventListener(CollectionFilters.EVENTS.CHANGE, CollectionFilters.syncFilterSelectionCount);
      window.addEventListener(CollectionFilters.EVENTS.POPSTATE, this.onHistoryChange.bind(this));
    }
  }

  /**
   * Initialize apply and sort buttons with their respective event handlers
   */
  initializeApplyButtons() {
    const applyBtn = this.querySelector(CollectionFilters.SELECTORS.APPLY_FILTERS);
    if (applyBtn) {
      applyBtn.addEventListener(CollectionFilters.EVENTS.CLICK, () => {
        const selectedCollectionFilter = this.filterForm?.querySelector('input[type="checkbox"]:checked[data-href]');
        const selectedTagFilter = this.filterForm?.querySelector('input[type="checkbox"]:checked[data-tagslist]');

        if (selectedCollectionFilter?.dataset.href) {
          this.handleCollectionFilterRedirect(selectedCollectionFilter);
        } else if (selectedTagFilter) {
          this.handleTagFilterSubmission();
        } else {
          this.closeFilterDrawer?.dispatchEvent(new Event(CollectionFilters.EVENTS.CLICK));
        }
      });
    }

    const applySortBtn = document.querySelector(CollectionFilters.SELECTORS.APPLY_SORTBY);
    if (applySortBtn) {
      applySortBtn.addEventListener(CollectionFilters.EVENTS.CLICK, () => {
        const selectedSortOption = document.querySelector(`${CollectionFilters.SELECTORS.SORTBY_OPTION}:checked`);
        if (selectedSortOption) {
          this.handleSortSelection(selectedSortOption.value);
        } else {
          this.closeSortDrawer();
        }
      });
    }
  }

  /**
   * Initialize sort option checkboxes for single selection behavior and clear functionality
   */
  initializeSortButtons() {
    const sortCheckboxes = document.querySelectorAll(CollectionFilters.SELECTORS.SORTBY_OPTION);
    sortCheckboxes.forEach(checkbox => {
      checkbox.addEventListener(CollectionFilters.EVENTS.CHANGE, function() {
        if (this.checked) {
          sortCheckboxes.forEach(otherCheckbox => {
            if (otherCheckbox !== this) {
              otherCheckbox.checked = false;
            }
          });
        }
      });
    });

    const clearSortBtn = document.querySelector(CollectionFilters.SELECTORS.CLEAR_SORTBY);
    if (clearSortBtn) {
      clearSortBtn.addEventListener(CollectionFilters.EVENTS.CLICK, (e) => {
        e.preventDefault();
        sortCheckboxes.forEach(checkbox => {
          checkbox.checked = false;
        });
      });
    }
  }

  /**
   * Initialize pagination links with click event handlers
   */
  initializePagination() {
    const paginationLinks = document.querySelectorAll(CollectionFilters.SELECTORS.PAGINATION_LINKS);
    paginationLinks.forEach(link => {
      link.addEventListener(CollectionFilters.EVENTS.CLICK, this._managePagination.bind(this));
    });
  }

  /**
   * Complete initialization by binding remaining events and applying styling
   */
  finalizeInitialization() {
    this.bindActiveFilterButtonEvents();
    this.reattachFilterEventListeners();
    this.colorOptionsStyling();
    CollectionFilters.syncFilterSelectionCount();
  }

  /**
   * Handle redirection for collection filter selection with sort parameter preservation
   * @param {HTMLElement} selectedFilter The selected collection filter element
   */
  handleCollectionFilterRedirect(selectedFilter) {
    const sortByField = this.filterForm?.querySelector('[name="sort_by"]');
    const currentSortBy = sortByField?.value || '';

    let redirectUrl = selectedFilter.dataset.href;
    if (currentSortBy.trim()) {
      const url = new URL(redirectUrl, window.location.origin);
      url.searchParams.set('sort_by', currentSortBy);
      redirectUrl = url.toString();
    }

    window.location.href = redirectUrl;
  }

  /**
   * Handle form submission for tag-based filters with drawer auto-close
   */
  handleTagFilterSubmission() {
    this.filterForm?.dispatchEvent(new Event(CollectionFilters.EVENTS.SUBMIT, {
      bubbles: true,
      cancelable: true
    }));
    
    setTimeout(() => {
      this.closeFilterDrawer?.dispatchEvent(new Event(CollectionFilters.EVENTS.CLICK));
    }, 1000);
  }

  /**
   * Handle sort selection by updating URL with sort parameter
   * @param {string} sortValue The selected sort value
   */
  handleSortSelection(sortValue) {
    const currentURL = new URL(window.location);
    currentURL.searchParams.set('sort_by', sortValue);
    window.location.href = currentURL.toString();
  }

  /**
   * Close the sort drawer and remove associated CSS classes
   */
  closeSortDrawer() {
    const sortDrawer = document.getElementById('sort-drawer');
    if (sortDrawer) {
      sortDrawer.classList.remove(CollectionFilters.CLASSES.FILTER_ACTIVE);
      document.body.classList.remove(CollectionFilters.CLASSES.MOBILEMENU_OPEN);
    }
  }

  /**
   * Filter Block Toggle event
   * @param {NodeList} toggleBtns Open filter block buttons
   */
  addAccessibilityAttributes(toggleBtns) {
    toggleBtns.forEach(element => {
      element.setAttribute('role', 'button');
      element.setAttribute('aria-expanded', false);
      element.setAttribute('aria-controls', element.nextElementSibling.id);
    });
  }

  /**
   * Collection filter form Input event
   * @param {event} event Event Instance
   */
  onSubmitHandler(event) {
    event.preventDefault();

    // Check if this is a collection-entry checkbox change that should redirect
    const isCollectionEntryChange = event.target && event.target.classList.contains('collection-entry') && event.target.checked;
    if(isCollectionEntryChange) {
      return; // Let the desktop event handler handle the redirect
    }

    let closestForm = document.getElementById('CollectionFiltersForm');
    if(event.target && event.target.closest('form')){ closestForm = event.target.closest('form'); }
    if(!closestForm) return;

    const searchParams = this._finalQueryString(closestForm)
    this.renderPage(searchParams);
  }

  /**
   * Build final query string from form data for URL updates
   * @param {HTMLFormElement} form Collection page filter form element
   * @returns {String} Formatted query string for URL parameters
   */
  _finalQueryString(form){
    const formData = new FormData(form);
    let searchParams = '';
    let constraints = '';
    formData.forEach((val, key) => {
      if(key.indexOf('price-list') >= 0){
        searchParams += '&' + val;
      }else if(key.indexOf('custom_sort_by') >= 0){
        //  Code for SortBy
      }else if(key.indexOf('tagsFilter-') > -1){
        if(constraints === ''){ constraints = val; }
        else{ constraints += `+${val}`; }
      }else if(key === 'sort_by' && val != null && val.length > 0){
        // Handle sort_by parameter
        if(searchParams === ''){ searchParams = key + '=' + val;}
        else{ searchParams += '&' + key + '=' + val; }
      }else if(val != null && val.length > 0){
        if(searchParams === ''){ searchParams = key + '=' + val;}
        else{ searchParams += '&' + key + '=' + val; }
      }
    });

    if(constraints != ''){
      if(searchParams == ''){
        searchParams = `constraint=${constraints}`;
      }else{
        searchParams += `&constraint=${constraints}`;
      }
    }

    return searchParams;
  }

  /**
   * Add background color styling to color filter options based on color handles
   */
  colorOptionsStyling(){
    const colorSwatchContainer = this.querySelector('[data-colorFilter]');
    if(colorSwatchContainer){
      const colorSwatches = colorSwatchContainer.querySelectorAll('.color-options');
      colorSwatches.forEach(swatch => {
        let colorHandle = swatch.querySelector('input[type="checkbox"]').dataset.handle;
        let swatchStyle = Utility.getSwatchStyle(colorHandle);
        swatch.querySelector('.option-label').setAttribute('style', swatchStyle);
      });
    }
  }

  /**
   * Handle click events on active filter clear buttons to remove filter selections
   * @param {Event} event Click event instance
   */
   onActiveFilterClick(event) {
    event.preventDefault();
    event.stopPropagation();
    let currentTarget = event.currentTarget;
    const closestClear =
      typeof event.target?.closest === 'function'
        ? event.target.closest('.filter-option-clear')
        : null;
    let URLString = null;
    if(currentTarget?.dataset?.type == 'tag_based'){
      const inputValue = currentTarget.dataset.remove;
      const inputElement  = document.querySelector(`input[data-tagslist][value="${inputValue}"]`);
      if(inputElement) inputElement.checked = false;

      let closestForm = document.getElementById('CollectionFiltersForm');
      if(!closestForm) return;
      URLString = this._finalQueryString(closestForm);
    }else{
      if(closestClear && closestClear.classList.contains('filter-option-clear')){ 
        currentTarget = closestClear;
        URLString = new URL(currentTarget.href).searchParams.toString();
      }
    }
    if(URLString != null)
      this.renderPage(URLString);
  }

   /**
   * Handle browser history changes to update filter state
   * @param {PopStateEvent} event Browser history change event instance
   */
  onHistoryChange(event) {
    const searchParams = event.state?.searchParams || '';
    this.renderPage(searchParams, false);
  }

  /**
   * Render filtered product page with new search parameters
   * @param {String} searchParams Query parameters for filtering
   * @param {Boolean} updateURLHash Whether to update browser URL history
   */
  renderPage(searchParams, updateURLHash = true) {
    document.getElementById('collection-product-grid').querySelector('#template-collection').classList.add('loading');

    //const url = `${window.location.pathname}?${searchParams}`;
    const url = searchParams && searchParams.length > 0 ? `${window.location.pathname}?${searchParams}` : window.location.pathname;
    this.renderGridFromFetch(url, 'filter');

    if (updateURLHash) this.updateURLHash(searchParams);
  }

  /**
   * Fetch and render product grid from server with AJAX
   * @param {String} url URL for fetching filtered results
   * @param {String} type Request type (filter / pagination / sub_collection_filter)
   */
  renderGridFromFetch(url, type) {
    if(!url) return;
    if (CollectionFilters._gridFetchController) {
      CollectionFilters._gridFetchController.abort();
    }
    CollectionFilters._gridFetchController = new AbortController();
    const { signal } = CollectionFilters._gridFetchController;

    fetch(url, { signal })
      .then((response) => response.text())
      .then((responseText) => {
        CollectionFilters._gridFetchController = null;
        this.renderProductGrid(responseText, type);
      })
      .catch((err) => {
        if (err.name === 'AbortError') return;
        CollectionFilters._gridFetchController = null;
      });
  }

  /**
   * Process and render the HTML response to update product grid and restore filter states
   * @param {String} html HTML response from server
   * @param {String} type Request type (filter / pagination / sub_collection_filter)
   */
  renderProductGrid(html, type) {
    const innerHTML = new DOMParser().parseFromString(html, 'text/html');

    // We have used individuals components replacement code because of load more feature
    let paginationType = 'numbers';
    if(document.querySelector('[data-pagination]')){
      paginationType = document.querySelector('[data-pagination]').dataset.type || 'numbers';
    }
    if(type == 'filter' || type == 'sub_collection_filter'){
      const gridHTML = innerHTML.getElementById('collection-product-grid').innerHTML;
      document.getElementById('collection-product-grid').innerHTML = gridHTML;

      if(type == 'sub_collection_filter'){
        let bannerHTML = innerHTML.querySelector('[data-collectionBanner]').innerHTML;
        document.querySelector('[data-collectionBanner]').innerHTML = bannerHTML;
      }
    }else{
      if(paginationType == 'loadmore'){
        let productGrids = innerHTML.getElementById('template-collection').innerHTML;
        let loadMoreBtn =  innerHTML.getElementById('load-more');
        document.getElementById('template-collection').insertAdjacentHTML('beforeend', productGrids);
        if(loadMoreBtn && document.getElementById('load-more')){ 
          document.getElementById('load-more').parentNode.replaceChild(loadMoreBtn, document.getElementById('load-more')); 
        }
        else if(document.getElementById('load-more')){ document.getElementById('load-more').remove(); }
      }else{
        const gridHTML = innerHTML.getElementById('collection-product-grid').innerHTML;
        document.getElementById('collection-product-grid').innerHTML = gridHTML;
      }
    }

    const openFilters = document.querySelectorAll('.filter__container.open');
    
    // Preserve filter type open states before HTML update
    const openFilterTypes = [];
    document.querySelectorAll('[data-filter-type][data-filter-main-categories].open').forEach(element => {
      openFilterTypes.push(element.getAttribute('data-filter-type'));
    });
    const openSubcategories = [];
    document.querySelectorAll('[data-filter-subcategories].open').forEach(element => {
      openSubcategories.push(element.getAttribute('data-filter-type'));
    });
    const isBackButtonVisible = document.querySelectorAll('[data-filter-back]:not(.d-none)').length > 0;
    const currentFilterLabel = document.querySelector('[data-filter-label]') ? document.querySelector('[data-filter-label]').innerHTML : '';
    
    const filterInnerHTML = innerHTML.getElementById('main-collection-filters').innerHTML;
    document.getElementById('main-collection-filters').innerHTML = filterInnerHTML;

    if(this.filterType != 'horizontal-filters'){
      // reopen filter blocks which are active before HTML update
      openFilters.forEach(openFilter => {
        if(openFilter.querySelector('.filter__toggle')){
          openFilter.querySelector('.filter__toggle').setAttribute('aria-expanded', true);
        }
        let filterBlock = document.querySelector('.filter__container[data-index="'+openFilter.dataset.index+'"]');
        filterBlock.classList.add('open');
        filterBlock.querySelector('.filter__block').classList.add('open');
      });
    }

    const filterContainers = document.querySelectorAll('.filter__container');
    filterContainers.forEach(filter => {
      const tagList = filter.querySelectorAll('.filter__item');
      if(tagList.length <= 0){
        filter.classList.add('d-none');
        filter.style.display = 'none';
      }
    });
    
    const activeFilters = innerHTML.querySelector('[data-activeFilters]');
    const activeFiltersTarget = document.querySelector('[data-activeFilters]');
    if (activeFilters && activeFiltersTarget) {
      activeFiltersTarget.innerHTML = activeFilters.innerHTML;
    }
    
    // Restore filter type open states BEFORE attaching event listeners
    if(openFilterTypes.length > 0) {
      openFilterTypes.forEach(filterType => {
        const mainCategory = document.querySelector(`[data-filter-type="${filterType}"][data-filter-main-categories]`);
        if(mainCategory) {
          mainCategory.classList.add('open');
        }
      });
    }
    
    if(openSubcategories.length > 0) {
      openSubcategories.forEach(filterType => {
        const subcategory = document.querySelector(`[data-filter-subcategories][data-filter-type="${filterType}"]`);
        if(subcategory) {
          subcategory.classList.add('open');
        }
      });
    }
    
    if(isBackButtonVisible) {
      document.querySelectorAll('[data-filter-back]').forEach(ele => {
        ele.classList.remove('d-none');
      });
    }
    
    if(currentFilterLabel && currentFilterLabel.trim() !== '') {
      const filterLabelElement = document.querySelector('[data-filter-label]');
      if(filterLabelElement) {
        filterLabelElement.innerHTML = currentFilterLabel;
      }
    }
    
    // Update filterForm reference after HTML update
    this.filterForm = this.querySelector('form');
    
    // Attach event listeners AFTER restoring states
    this.bindActiveFilterButtonEvents();
    this.reattachFilterEventListeners();

    CollectionFilters.syncFilterSelectionCount();
    
    if(this.quickshop){this.quickshop.updateEvents();}
  }

  /**
   * Re-bind event listeners on active filter clear buttons after AJAX content update
   */
  bindActiveFilterButtonEvents() {
    document.querySelectorAll('.filter-option-clear').forEach((element) => {
      element.addEventListener('click', this.onActiveFilterClick, { once: true });
    });
  }

  /**
   * Re-attach all filter event listeners after HTML content update via AJAX
   */
  reattachFilterEventListeners() {
    // Re-attach filter toggle button event listeners
    const self = this;
    const toggleBtns = this.querySelectorAll('.filter__toggle');
    toggleBtns.forEach(button => {
      button.addEventListener('click', self.toggleFilterBlock.bind(self));
    });
    this.addAccessibilityAttributes(toggleBtns);

    // Re-attach form event listeners
    if(this.filterForm){
      this.filterForm.addEventListener('input', this.debouncedOnSubmit.bind(this));
      this.filterForm.addEventListener('submit', this.debouncedOnSubmit.bind(this));
      this.filterForm.addEventListener('input', CollectionFilters.syncFilterSelectionCount);
      this.filterForm.addEventListener('change', CollectionFilters.syncFilterSelectionCount);
    }

    // Re-attach horizontal filter event listeners if needed
    if(this.filterType == 'horizontal-filters'){
      const filterContainers = this.querySelectorAll('.filter__container');
      filterContainers.forEach((target) => {
        target.addEventListener('click', (event)=>{
          event.stopPropagation();
        });
      });
    }

    // Re-attach subcollection dropdown click-outside-to-close functionality
    if (typeof this.initializeSubcollectionDropdowns === 'function') {
      this.initializeSubcollectionDropdowns();
    }

    // Re-attach collection-entry checkbox event listeners
    this.querySelectorAll('input[type="checkbox"][data-tagsList], input[type="checkbox"].collection-entry').forEach(function(checkbox) {
      checkbox.addEventListener('change', function() {
        if (this.checked) {
          // Uncheck all other checkboxes
          document.querySelectorAll('input[type="checkbox"][data-tagsList], input[type="checkbox"].collection-entry').forEach(function(otherCheckbox) {
            if (otherCheckbox !== checkbox) {
              otherCheckbox.checked = false;
            }
          });

          // On desktop (sidebar), redirect immediately
          if (window.innerWidth >= 992) { // Desktop breakpoint
            const href = this.getAttribute('data-href');
            if (href) {
              // Get current sort_by value from the form using the component's form reference
              const sortByField = self.filterForm ? self.filterForm.querySelector('[name="sort_by"]') : null;
              const currentSortBy = sortByField ? sortByField.value : '';

              // Build URL with sort_by parameter if it exists
              let redirectUrl = href;
              if(currentSortBy && currentSortBy.trim() !== '') {
                const url = new URL(redirectUrl, window.location.origin);
                url.searchParams.set('sort_by', currentSortBy);
                redirectUrl = url.toString();
              }

              window.location.href = redirectUrl;
            }
          }
        }
      });
    });

    // Re-attach filter type category event listeners
    const filterTypeButtons = this.querySelectorAll("[data-filter-type][data-filter-main-categories]");
    if(filterTypeButtons.length > 0){
      filterTypeButtons.forEach(function(element) {
        element.addEventListener('click', function(event) {
          event.preventDefault();
          const filterType = this.getAttribute('data-filter-type');
          const content = document.querySelector(`[data-filter-subcategories][data-filter-type="${filterType}"]`);

          // Check if this filter type is already open by checking both the main element and content
          const isMainCategoryOpen = this.classList.contains('open');
          const isContentOpen = content && content.classList.contains('open');
          const isCurrentlyOpen = isMainCategoryOpen && isContentOpen;

          if (isCurrentlyOpen) {
            // Close the current filter type
            if (content) {
              content.classList.remove('open');
            }
            this.classList.remove('open');
            document.querySelectorAll("[data-filter-back]").forEach(ele=> {ele.classList.add('d-none');});
            // Reset the label to default
            const filterLabelElement = document.querySelector("[data-filter-label]");
            const defaultLabel = filterLabelElement ? (filterLabelElement.getAttribute('data-label') || 'Filter') : 'Filter';
            if (filterLabelElement) {
              filterLabelElement.innerHTML = defaultLabel;
            }
          } else {
            // Close all other filter types first
            document.querySelectorAll("[data-filter-type][data-filter-main-categories]").forEach(ele=> {ele.classList.remove('open');});
            document.querySelectorAll("[data-filter-subcategories]").forEach(ele=> {ele.classList.remove('open');});

            // Open the clicked filter type
            if (content) {
              content.classList.add('open');
              this.classList.add('open');
              const filterLabelElement = document.querySelector("[data-filter-label]");
              if (filterLabelElement) {
                filterLabelElement.innerHTML = filterType;
              }
              document.querySelectorAll("[data-filter-back]").forEach(ele=> {ele.classList.remove('d-none');});
            }
          }
        });
      });
    }

    // Re-attach filter back button event listeners
    const filterBackButtons = document.querySelectorAll("[data-filter-back]");
    if(filterBackButtons.length > 0){
      filterBackButtons.forEach(function(element) {
        // Check if listener is already attached to prevent duplicates
        if (!element.hasAttribute('data-listener-attached')) {
          element.setAttribute('data-listener-attached', 'true');
          element.addEventListener('click', function(event) {
            event.preventDefault();
            let currentEle = event.currentTarget;
            currentEle.classList.add('d-none');
            document.querySelectorAll("[data-filter-type]").forEach(ele=> {ele.classList.remove('open');});
            document.querySelectorAll("[data-filter-main-categories][data-filter-type]").forEach(ele=> {ele.classList.remove('open');});
            // Reset the label to default
            const filterLabelElement = document.querySelector("[data-filter-label]");
            const defaultLabel = filterLabelElement ? (filterLabelElement.getAttribute('data-label') || 'Filter') : 'Filter';
            if (filterLabelElement) {
              filterLabelElement.innerHTML = defaultLabel;
            }
          });
        }
      });
    }

    // Re-attach clear all filters button
    const clearAllBtn = this.querySelector('#clearAllFilters');
    if (clearAllBtn) {
      clearAllBtn.addEventListener('click', function(e) {
        e.preventDefault();
        // Uncheck all filter checkboxes within this component
        self.querySelectorAll('input[type="checkbox"][data-tagsList], input[type="checkbox"].collection-entry').forEach(function(checkbox) {
          checkbox.checked = false;
        });
        CollectionFilters.syncFilterSelectionCount();
      });
    }

    // Re-attach sort by checkbox event listeners
    this.querySelectorAll('input[type="checkbox"].sortby-option').forEach(function(checkbox) {
      checkbox.addEventListener('change', function() {
        if (this.checked) {
          // Uncheck all other sort options
          self.querySelectorAll('input[type="checkbox"].sortby-option').forEach(function(otherCheckbox) {
            if (otherCheckbox !== checkbox) {
              otherCheckbox.checked = false;
            }
          });
        }
      });
    });

    // Re-attach clear all sort button
    const clearAllSortBtn = this.querySelector('#clearAllSort');
    if (clearAllSortBtn) {
      clearAllSortBtn.addEventListener('click', function(e) {
        e.preventDefault();
        // Uncheck all sort checkboxes
        self.querySelectorAll('input[type="checkbox"].sortby-option').forEach(function(checkbox) {
          checkbox.checked = false;
        });
      });
    }

    // Re-attach mobile sort by radio button event listeners
    const mobileForm = document.getElementById('SortByFormMobile');
    if (mobileForm) {
      const mobileRadios = mobileForm.querySelectorAll('input[type="radio"][name="sort_by"]');

      mobileRadios.forEach(function(radio) {
        radio.addEventListener('change', function() {
          if (this.checked) {
            const sortValue = this.value;

            // Find the sort_by field in the main filter form and update it using component's form reference
            const sortByField = self.filterForm ? self.filterForm.querySelector('[name="sort_by"]') : null;

            if (sortByField && self.filterForm) {
              sortByField.value = sortValue;

              // Close the sort drawer first
              const sortDrawer = document.getElementById('sort-drawer');
              if(sortDrawer) {
                sortDrawer.classList.add('d-none');
                sortDrawer.classList.remove('sortby-active');
              }
              document.body.classList.remove('drawer-open');

              // Hide the overlay
              if(typeof siteOverlay !== 'undefined') {
                siteOverlay.prototype.hideOverlay();
              }

              // Trigger the form submission to update the page with new sort using AJAX
              self.filterForm.dispatchEvent(new Event('submit', {
                bubbles: true,
                cancelable: true,
              }));
            } else {
              // Fallback: update URL directly if main form not found
              const currentURL = new URL(window.location);
              currentURL.searchParams.set('sort_by', sortValue);
              window.location.href = currentURL.toString();
            }
          }
        });
      });
    }


  }

  /**
   * Update browser URL and history with new search parameters
   * @param {String} searchParams Query parameters to add to URL
   */
  updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  /**
   * Handle sort by option changes and update page accordingly
   * @param {Event} event Change event from sort option input
   */
  updateSortBy(event){
    let _this = event.currentTarget;
    let currentvalue
    if(_this.name == 'custom_sort_by_mobile'){
        currentvalue = document.querySelector('[data-sortby] [name="custom_sort_by_mobile"]:checked').value;
      }
    if(_this.name == 'custom_sort_by_desktop'){
      currentvalue = document.querySelector('[data-sortby] [name="custom_sort_by_desktop"]:checked').value;
    }
    
    // Remove active class from sorting dropdown for all sorting interactions
    const sortingDropdown = document.querySelector("[sorting-dropdown]");
    if(sortingDropdown) {
      sortingDropdown.classList.remove("active");
    }



    // Find the sort_by field in the main filter form
    const sortByField = this.filterForm ? this.filterForm.querySelector('[name="sort_by"]') : null;
    if(sortByField) {
      sortByField.value = currentvalue;
      // Trigger the form submission to update the page with new sort
      this.filterForm.dispatchEvent(new Event('submit', {
        bubbles: true,
        cancelable: true,
      }));
    } else {
      // Fallback: update URL directly if sort_by field not found
      const currentURL = new URL(window.location);
      currentURL.searchParams.set('sort_by', currentvalue);
      window.location.href = currentURL.toString();
    }
    
  }

  /**
   * Handle pagination option changes and update product count
   * @param {Event} event Change event from pagination input
   */
  updatePagination(event){
    event.preventDefault();
    let _this = event.currentTarget;
    let currentvalue = document.querySelector('[data-custom-pagination] [type="radio"]:checked').value;

    this.querySelector('[name="count"]').value = currentvalue;
    this.querySelector('[name="count"]').dispatchEvent(new Event('input', {
      bubbles: true,
      cancelable: true,
    }));

  }
  /**
   * Handle sub-collection navigation and filter state preservation
   * @param {Event} event Click event from sub-collection link
   */
  _manageSubCollections(event){
    event.preventDefault();
    const _this =  event.currentTarget;
    let closestForm = document.getElementById('CollectionFiltersForm');
    if(_this.closest('form')){ closestForm = event.target.closest('form'); }
    const queryString = this._finalQueryString(closestForm);

    let collURL = _this.dataset.href + '?' + queryString;
    this.renderGridFromFetch(collURL, 'sub_collection_filter');
    setTimeout(() => {
      history.pushState({}, '', collURL);
    }, 500);
    if(window.innerWidth <= 767){
      setTimeout(() => {
        this.closeFilterDrawer.dispatchEvent(new Event('click'));
      }, 1000);
    }
  }

  /**
   * Handle pagination link clicks and scroll to top
   * @param {Event} event Click event from pagination link
   */
  _managePagination(event){
    event.preventDefault();
    let _this = event.currentTarget;
    var nextPageLink = _this.href;
    var product_count=parseInt(event.target.closest('[data-pagination_bar]')?.getAttribute('data-pagination_bar'))
    if (nextPageLink && nextPageLink.includes('?') && nextPageLink.includes('=')) {
      nextPageLink=nextPageLink+`&count=${product_count}`
    }
    document.body.scrollTop = 0; // For Safari
    document.documentElement.scrollTop = 0;
    this.renderGridFromFetch(nextPageLink, 'pagination');

    setTimeout(() => {
      history.pushState({}, '', nextPageLink);
    }, 500);
  }

  /**
   * Toggle filter drawer open/close state with accessibility and focus management
   * @param {Event} event Click event from drawer toggle button
   */
  toggleFilterDrawer(event){
    event.preventDefault();
    let button = event.currentTarget;
    if(button.id == 'filter-drawer'){
      this.filterParent.classList.add('filter-active');
      button.setAttribute('aria-expanded', true);
      button.setAttribute('tabindex', '-1');

      Utility.trapFocus(this.filterParent);
      Utility.forceFocus(this.closeFilterDrawer);
      siteOverlay.prototype.showOverlay();
    }else{
      this.filterParent.classList.remove('filter-active');
      if(this.openFilterDrawer){
        this.openFilterDrawer.setAttribute('aria-expanded', false);
        this.openFilterDrawer.removeAttribute('tabindex');
      }

      Utility.removeTrapFocus(this.filterParent);
      siteOverlay.prototype.hideOverlay();
    }
  }

  /**
   * Toggle sort by drawer open/close state with accessibility and focus management
   * @param {Event} event Click event from sort drawer toggle button
   */
  toggleSortDrawer(event){
    event.preventDefault();
    let button = event.currentTarget;
    const sortDrawer = document.getElementById('sort-drawer');
    const sortCloseBtn = sortDrawer ? sortDrawer.querySelector('.collection-filters-close') : null;

    if(button.id == 'sortby-drawer'){
             if(sortDrawer) {
         sortDrawer.classList.add('filter-active');
         document.body.classList.add('mobilemenu-open');
         
         // Add click-outside-to-close functionality
         const self = this;
         
         // Remove any existing click handler first
         if(this.sortDrawerClickHandler) {
           document.removeEventListener('click', this.sortDrawerClickHandler);
         }
         
         // Add new click handler
         this.sortDrawerClickHandler = function(clickEvent) {
           // Check if click is outside the sort drawer
           if (!sortDrawer.contains(clickEvent.target) && 
               !clickEvent.target.closest('#sortby-drawer')) {
             // Close the sort drawer
             if(sortCloseBtn) {
               sortCloseBtn.click();
             }
           }
         };
         
         // Add event listener with delay to prevent immediate closing
         setTimeout(() => {
           document.addEventListener('click', this.sortDrawerClickHandler);
         }, 300);
       }
      button.setAttribute('aria-expanded', true);
      button.setAttribute('tabindex', '-1');

      if(sortDrawer) Utility.trapFocus(sortDrawer);
      if(sortCloseBtn) Utility.forceFocus(sortCloseBtn);
      if (window.globalVariables && window.globalVariables.template === 'collection') {
         document.getElementById('site-overlay').classList.add('sortby-overlay');
         }
      siteOverlay.prototype.showOverlay();
         }else{
       // Clean up click-outside event listener
       if(this.sortDrawerClickHandler) {
         document.removeEventListener('click', this.sortDrawerClickHandler);
         this.sortDrawerClickHandler = null;
       }
       
       if(sortDrawer) {
         sortDrawer.classList.remove('filter-active');
         document.body.classList.remove('mobilemenu-open');
       }
      if(this.openSortbyDrawer){
        this.openSortbyDrawer.setAttribute('aria-expanded', false);
        this.openSortbyDrawer.removeAttribute('tabindex');
      }

      if(sortDrawer) Utility.removeTrapFocus(sortDrawer);
      siteOverlay.prototype.hideOverlay();
    }
  }

  /**
   * Toggle individual filter block open/close state
   * @param {Event} event Click event from filter block toggle button
  */
  toggleFilterBlock(event) {
    event.preventDefault();
    const toggleFilterBtn = event.currentTarget;
    const filterContainer = toggleFilterBtn.parentNode;
    const isOpen = filterContainer.classList.contains('open');
    isOpen ? this.closeFilterBlock(toggleFilterBtn) : this.openFilterBlock(toggleFilterBtn);
  }

  /**
   * Open a specific filter block with accordion behavior and focus management
   * @param {HTMLElement} toggleFilterBtn Filter toggle button element
   */
  openFilterBlock(toggleFilterBtn){
    let filterContainer = toggleFilterBtn.parentNode;
    toggleFilterBtn.setAttribute('aria-expanded', true);
    Utility.toggleElement(filterContainer, 'open');
    if(this.accordionType == 'single'){
      let siblingBlocks = filterContainer.parentNode.querySelectorAll('.filter__container');
      siblingBlocks.forEach(element => {
        if(element == filterContainer){ return; }
        element.classList.remove('open');
        element.querySelector('.filter__block').classList.remove('open');
        element.querySelector('.filter__toggle').setAttribute('aria-expanded', false);
      });
    }
    Utility.trapFocus(filterContainer);
  }

  /**
   * Close a specific filter block and remove focus trap
   * @param {HTMLElement} toggleFilterBtn Filter toggle button element
   */
  closeFilterBlock(toggleFilterBtn){
    const filterContainer = toggleFilterBtn.parentNode;
    toggleFilterBtn.setAttribute('aria-expanded', false);
    Utility.toggleElement(filterContainer, 'close');
    Utility.removeTrapFocus(filterContainer);
  }

  /**
   * Cleanup when component is removed from DOM
   */
  disconnectedCallback() {
    // Clean up subcollection dropdown event listeners
    if (typeof this.cleanupSubcollectionDropdowns === 'function') {
      this.cleanupSubcollectionDropdowns();
    }
    
    // Clean up sort drawer click handler if it exists
    if (this.sortDrawerClickHandler && 
        document && 
        typeof document.removeEventListener === 'function') {
      document.removeEventListener('click', this.sortDrawerClickHandler);
      this.sortDrawerClickHandler = null;
    }
  }
}

customElements.define('collection-filters', CollectionFilters);

